import { useState } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { useAuth } from "@/context/AuthContext";
import { startCheckout } from "@/lib/checkout";
import { Globe, Server, Check } from "lucide-react";

export default function Pricing() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [gb, setGb] = useState([25]);
  const [ips, setIps] = useState([25]);

  const buy = (product, quantity) => {
    if (!user) { nav("/register"); return; }
    startCheckout({ product, quantity });
  };

  return (
    <div className="min-h-screen text-ink">
      <Navbar />
      <div className="max-w-6xl mx-auto px-6 pt-32 pb-24">
        <div className="text-center mb-16 fade-up">
          <p className="font-mono text-xs text-neon tracking-[0.3em] uppercase mb-3">// Pricing</p>
          <h1 className="font-head text-4xl sm:text-5xl font-extrabold tracking-tighter">Pay only for what you use.</h1>
          <p className="text-zinc-600 mt-4">Residential data never expires · ISPs billed monthly, cancel anytime.</p>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          {/* Residential */}
          <div className="led-border led-fast bg-surface border border-[#CBEEF5] rounded-xl p-8 glow-cyan relative">
            <span className="absolute -top-3 left-8 bg-neon text-base text-xs font-mono font-bold px-3 py-1 rounded">MOST POPULAR</span>
            <Globe className="w-7 h-7 text-neon mb-4" aria-hidden="true" />
            <h2 className="font-head text-2xl font-bold">Residential</h2>
            <div className="mt-3 mb-6"><span className="font-mono text-4xl text-neon">$6</span><span className="text-zinc-500 font-mono"> / GB</span></div>
            <ul className="space-y-2 text-sm text-zinc-600 mb-8">
              {["150M+ rotating IPs", "Sticky & rotating sessions", "195 countries · city targeting", "Data never expires"].map((f) => (
                <li key={f} className="flex items-center gap-2"><Check className="w-4 h-4 text-neon" aria-hidden="true" /> {f}</li>
              ))}
            </ul>
            <div className="flex justify-between font-mono text-sm mb-2"><span className="text-zinc-500">Amount</span><span className="text-ink">{gb[0]} GB</span></div>
            <Slider data-testid="resi-gb-slider" value={gb} onValueChange={setGb} min={1} max={500} step={1} className="mb-6" />
            <div className="flex items-center justify-between mb-4">
              <span className="text-zinc-500 text-sm">Total</span>
              <span className="font-mono text-2xl text-neon">${(gb[0] * 6).toFixed(2)}</span>
            </div>
            <Button onClick={() => buy("residential_gb", gb[0])} data-testid="buy-residential-button" className="w-full bg-neon text-base hover:bg-neon-hover font-semibold h-11">Buy Residential</Button>
          </div>

          {/* ISP */}
          <div className="led-border bg-surface border border-[#CBEEF5] rounded-xl p-8">
            <Server className="w-7 h-7 text-lime mb-4" aria-hidden="true" />
            <h2 className="font-head text-2xl font-bold">ISP</h2>
            <div className="mt-3 mb-6"><span className="font-mono text-4xl text-lime">$2</span><span className="text-zinc-500 font-mono"> / IP / mo</span></div>
            <ul className="space-y-2 text-sm text-zinc-600 mb-8">
              {["Dedicated static IPs · Ashburn, VA", "Unlimited bandwidth", "Same IPs kept every renewal", "Billed monthly · min 25 IPs"].map((f) => (
                <li key={f} className="flex items-center gap-2"><Check className="w-4 h-4 text-lime" aria-hidden="true" /> {f}</li>
              ))}
            </ul>
            <div className="flex justify-between font-mono text-sm mb-2"><span className="text-zinc-500">Quantity</span><span className="text-ink">{ips[0]} IPs</span></div>
            <Slider data-testid="isp-ip-slider" value={ips} onValueChange={(v) => setIps([Math.max(25, v[0])])} min={25} max={500} step={5} className="mb-6" />
            <div className="flex items-center justify-between mb-1">
              <span className="text-zinc-500 text-sm">Total</span>
              <span className="font-mono text-2xl text-lime">${(ips[0] * 2).toFixed(2)}<span className="text-zinc-500 text-base"> /mo</span></span>
            </div>
            <p className="text-xs text-zinc-400 mb-4">Billed monthly · cancel anytime</p>
            <Button onClick={() => buy("isp_ip", ips[0])} data-testid="buy-isp-button" className="w-full bg-lime text-base hover:bg-lime/90 font-semibold h-11">Subscribe</Button>
          </div>
        </div>
      </div>
    </div>
  );
}
